import React from 'react';
import { any, string } from 'prop-types';
import { keyBy, get, filter, map, orderBy } from 'lodash';
import QueryManager from './Query';
import gql from 'graphql-tag';

const QUERY_GET_AUDIT_HISTORY = gql`query getAuditHistory($orderId: String!) {
    getAuditHistory(orderId: $orderId) {
        orderId
        order {
            id
            items {
                id
                ean
                name
                imageUrl
                sku
                brandName
                listPrice
                quantity
                sellingPrice
                basePrice
                customerNotes
                unitMultiplier
                substitution {
                    label
                    code
                }
                priceTags {
                    value
                    rateAndBenefit {
                        name
                    }
                }
                unit
            }
        }
        auditHistory {
            createdBy
            createdAt
            audits {
                results {
                    audited {
                        productId
                        failed
                        audits {
                            codeRule
                            decision
                            value
                        }
                    }
                }
            }
            weightError
            picking {
                isAudit
                item {
                    productId
                    brandName
                    sku
                    refId
                    manualPicking
                    unpackaged
                    ean
                    price
                    name
                    imageUrl
                    status
                    unitMultiplier
                    pickingQuantity
                    invoicePrice
                    sellingPrice
                    excess
                    unit
                    substitution {
                        productId
                        brandName
                        sku
                        manualPicking
                        unpackaged
                        ean
                        price
                        name
                        imageUrl
                        status
                        unitMultiplier
                        pickingQuantity
                        invoicePrice
                        sellingPrice
                        excess
                        unit
                    }
                }
            }
        }
    }
}`;

const pickingItemReducer = ({ item, isAudit }) => { // This function lets set id to added by client items, due to each of that comes with sku undefined
    const { status, sku, refId } = item;
    const isAddedByClient = status === 'ADDED_BY_CLIENT';
    return {
        isAudit,
        item: {
            ...item,
            sku: isAddedByClient ? refId : sku
        }
    };
};

const formatResponse = (data) => {
    // audits -> [audited] -> was audited by a person
    // audited -> [productId(pickedItem.sku)]
    // item -> [orderItems(audited.productId  === item.id])]
    const orderItems = get(data, 'getAuditHistory.order.items', []);
    const orderItemsById = keyBy(orderItems, 'id');
    const auditResults = get(data, 'getAuditHistory.auditHistory', {});
    const audits = map(auditResults, (response) => { // Map as many audits has the order
        const pickingItems = get(response, 'picking', []);
        return {
            weightError: get(response, 'weightError', 'null'),
            audits: get(response, 'audits.results'),
            picking:
                keyBy(
                    map(
                        keyBy(map(pickingItems, pickingItemReducer), 'item.sku'),
                        ({ isAudit, item }, key) => ({
                            isAudit,
                            picking: item,
                            id: get(item, 'sku'),
                            ...get(orderItemsById, key)
                        })
                    ),
                'id'),
            createdAt: get(response, 'createdAt'),
            createdBy: get(response, 'createdBy')
        };
    });

    const auditHistory = orderBy(audits, ({ createdAt }) => {
        return new Date(createdAt).getTime();
    }, ['desc']);
    return auditHistory;
};

const AuditHistory = ({ children, orderId, ...props }) => {
    return (
        <QueryManager query={QUERY_GET_AUDIT_HISTORY} {...props} variables={{ orderId }} fetchPolicy='network-only'>
            {({ data, loading, fetchMore, refetch }) => {
                return children({
                    data: {
                        auditHistory: formatResponse(data)
                    },
                    loading
                });
            }}
        </QueryManager>
    );
};

AuditHistory.propTypes = {
    children: any,
    orderId: string
};

export default AuditHistory;
